import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Lock, Clock, ShieldCheck, BadgeCheck, Flame } from "lucide-react";
import { INVESTMENTS as MOCK_INVESTMENTS } from "../mock";
import { getIcon } from "../lib/icons";
import { getOffers } from "../lib/api";

export default function Hero() {
  const [offers, setOffers] = useState(MOCK_INVESTMENTS);

  useEffect(() => {
    let alive = true;
    getOffers().then((data) => { if (alive && data?.length) setOffers(data); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const featured = offers.filter((i) => i.hot).slice(0, 4);

  return (
    <section className="relative overflow-hidden bg-[#0d1a2e] text-white">
      <div className="absolute inset-0 bg-grid-dark opacity-40 pointer-events-none" />
      <div className="absolute -top-20 -right-32 w-[44rem] h-[44rem] glow-emerald pointer-events-none" />
      <div className="absolute bottom-0 -left-32 w-[30rem] h-[30rem] glow-emerald-soft pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-12 lg:pt-24 pb-16 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-7 reveal">
            <div className="inline-flex items-center gap-2 text-[12px] font-semibold bg-white/[0.06] border border-white/10 rounded-full pl-2 pr-4 py-1.5 text-white/85">
              <span className="w-2 h-2 rounded-full bg-[#16d196] pulse-dot" />
              FCA-aware · Q3 2025 offers open
            </div>

            <h1 className="mt-5 lg:mt-6 text-[36px] sm:text-[52px] lg:text-[78px] font-extrabold leading-[1.04] lg:leading-[1.02] tracking-[-0.025em]">
              Invest in{" "}
              <span className="text-[#16d196] underline decoration-[#16d196] decoration-[5px] lg:decoration-[6px] underline-offset-[5px] lg:underline-offset-[6px] decoration-skip-ink-none">
                what Britain&apos;s building.
              </span>
            </h1>
            <p className="mt-5 lg:mt-6 text-white/70 text-[15.5px] lg:text-[18px] max-w-xl leading-relaxed">
              The UK&apos;s trusted hub for vetted property, bonds, ISAs, SIPPs, VCT/EIS and
              renewable infrastructure. Get matched to the right offer in under 60
              seconds. No cold calls. No commission bias.
            </p>

            <div className="mt-7 lg:mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/apply">
                <button className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 bg-[#16d196] hover:bg-[#13b884] text-[#0d1a2e] rounded-full h-13 lg:h-14 px-6 lg:px-7 text-[15px] font-bold transition-colors shadow-[0_8px_24px_rgba(22,209,150,0.35)]">
                  Get matched in 60s
                  <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
                </button>
              </Link>
              <Link to="/#how-it-works">
                <button className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 bg-transparent border border-white/20 hover:border-white/40 text-white rounded-full h-14 px-7 text-[15px] font-semibold transition-colors">
                  How it works
                </button>
              </Link>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
              {[
                { Icon: Lock, label: "No upfront fees" },
                { Icon: Clock, label: "60-second match" },
                { Icon: ShieldCheck, label: "GDPR-secure" },
                { Icon: BadgeCheck, label: "FCA-aware" },
              ].map(({ Icon, label }) => (
                <div key={label} className="flex items-center gap-2 text-[13px] text-white/75">
                  <Icon className="w-4 h-4 text-[#16d196]" strokeWidth={2.4} />
                  {label}
                </div>
              ))}
            </div>
          </div>

          {/* Right side: interactive picker card */}
          <div className="lg:col-span-5 reveal" style={{ animationDelay: "120ms" }}>
            <div className="relative bg-white rounded-3xl p-7 lg:p-8 shadow-[0_20px_60px_rgba(0,0,0,0.25)]">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#16d196]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#16d196]" />
                Start in seconds
              </div>
              <h3 className="mt-3 text-[22px] font-extrabold text-[#0d1a2e] tracking-tight">
                What type of investment fits you?
              </h3>
              <p className="text-[13.5px] text-[#0d1a2e]/55 mt-1">
                Pick a category to launch your free 60-second match.
              </p>

              <div className="mt-5 grid grid-cols-2 gap-2.5">
                {featured.map((inv) => {
                  const Icon = getIcon(inv.icon);
                  return (
                    <Link
                      key={inv.slug}
                      to={`/investments/${inv.slug}`}
                      className="group relative bg-[#f4f8f7] hover:bg-white hover:shadow-md hover:border-[#16d196]/40 border border-[#0d1a2e]/8 rounded-2xl p-4 transition-all"
                    >
                      <div className="w-9 h-9 rounded-lg bg-[#e3f7ee] flex items-center justify-center">
                        <Icon className="w-4.5 h-4.5 text-[#0d1a2e]" strokeWidth={2.2} />
                      </div>
                      <div className="mt-3 text-[13.5px] font-bold text-[#0d1a2e] leading-tight">
                        {inv.name}
                      </div>
                      <div className="text-[11.5px] text-[#0d1a2e]/55 mt-0.5">
                        {inv.yieldRange}
                      </div>
                    </Link>
                  );
                })}
              </div>

              <Link to="/investments" className="block mt-3">
                <button className="w-full inline-flex items-center justify-center gap-1.5 bg-[#16d196] hover:bg-[#13b884] text-[#0d1a2e] rounded-full h-13 py-3.5 text-[14.5px] font-bold transition-colors">
                  See all 11 investments
                  <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
                </button>
              </Link>

              <div className="mt-4 flex items-center gap-3">
                <div className="flex -space-x-2">
                  {["J", "P", "M", "A"].map((l, i) => (
                    <div
                      key={i}
                      className={`w-7 h-7 rounded-full text-[11px] font-bold flex items-center justify-center border-2 border-white ${
                        ["bg-[#fde68a]", "bg-[#fecaca]", "bg-[#bfdbfe]", "bg-[#d9f99d]"][i]
                      } text-[#0d1a2e]`}
                    >
                      {l}
                    </div>
                  ))}
                </div>
                <div className="text-[12px] text-[#0d1a2e]/60">
                  <span className="font-bold text-[#0d1a2e]">4,200+</span> UK investors
                  matched to date
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trust strip */}
      <div className="relative bg-white border-t border-[#0d1a2e]/8">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-4 lg:gap-x-8 items-center justify-items-start lg:justify-items-center">
            {[
              { Icon: BadgeCheck, t: "FCA Aware", s: "Compliance-led ops" },
              { Icon: ShieldCheck, t: "SRA Advisors", s: "Specialist legal panel" },
              { Icon: Flame, t: "Vetted Offers", s: "<6% pass our screen" },
              { Icon: Lock, t: "GDPR & ICO", s: "Bank-level security" },
              { Icon: BadgeCheck, t: "5★ Trustpilot", s: "Rated Excellent" },
              { Icon: BadgeCheck, t: "£612m+ Placed", s: "For our investors" },
            ].map(({ Icon, t, s }) => (
              <div key={t} className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-[#e3f7ee] flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-[#16d196]" strokeWidth={2.4} />
                </div>
                <div className="leading-tight min-w-0">
                  <div className="text-[12.5px] font-bold text-[#0d1a2e] truncate">{t}</div>
                  <div className="text-[11px] text-[#0d1a2e]/55 truncate">{s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
